import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AlertCircle } from 'lucide-react';
import { PageLoader } from './PageLoader';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  fallbackPath?: string;
}

export function ProtectedRoute({
  children,
  allowedRoles = [],
  fallbackPath = '/home'
}: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();

  // Show loading spinner while checking auth (rendered inside the layout's
  // content area, so use the content-centered variant, not fullScreen).
  if (isLoading) {
    return <PageLoader />;
  }

  // If user is not authenticated, they'll be redirected by useAuth hook
  if (!user) {
    return null;
  }

  // Check role-based access: if allowedRoles is provided and non-empty, user's role must be in the list
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    const formattedRoles = allowedRoles.map(role =>
      role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    ).join(' or ');

    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto p-8">
          <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access this page. This feature is only available to {formattedRoles} users.
          </p>
          <Navigate to={fallbackPath} replace />
        </div>
      </div>
    );
  }

  return <>{children}</>;
} 